// Tenant context — loads /api/tenants/me once after auth and exposes the
// industry-template intake schemas to every page via the useTenant() hook.
//
// Lives in its own file (not lib/auth.ts) because tenant data is per-session
// per-user, not per-token. Auth knows who you are; this knows what you sell.

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { apiGet } from './api';

export type FieldType = 'text' | 'number' | 'textarea' | 'select' | 'boolean';

export interface IntakeField {
  key: string;
  label: string;
  type: FieldType;
  options?: string[];
  required?: boolean;
}

export interface Tenant {
  id: number;
  slug: string;
  name: string;
  timezone: string;
  template: string;
  template_label: string;
  customer_fields: IntakeField[];
  booking_fields: IntakeField[];
  settings: Record<string, unknown>;
}

interface TenantContextValue {
  tenant: Tenant | null;
  loading: boolean;
  error: string | null;
  reload: () => Promise<void>;
}

const TenantContext = createContext<TenantContextValue>({
  tenant: null,
  loading: true,
  error: null,
  reload: async () => {},
});

export function TenantProvider({ children }: { children: ReactNode }) {
  const [tenant, setTenant] = useState<Tenant | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await apiGet<{ tenant: Tenant }>('/api/tenants/me');
      setTenant(r.tenant);
    } catch (e) {
      setError((e as Error).message);
      setTenant(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    reload();
  }, [reload]);

  const value = useMemo<TenantContextValue>(
    () => ({ tenant, loading, error, reload }),
    [tenant, loading, error, reload]
  );

  return <TenantContext.Provider value={value}>{children}</TenantContext.Provider>;
}

export function useTenant(): TenantContextValue {
  return useContext(TenantContext);
}
